import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Mail, Lock, User, Briefcase, Loader2, CheckCircle2 } from 'lucide-react';

const ROLES = [
  { id: 'employee', label: 'Employee' },
  { id: 'agent', label: 'Agent' },
  { id: 'manager', label: 'Manager' },
];

const PortalRegister = () => {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '', requested_role: 'employee' });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (form.password.length < 6) return setErr('Password must be at least 6 characters');
    if (form.password !== form.confirm) return setErr('Passwords do not match');
    setLoading(true);
    try {
      await register({ name: form.name.trim(), email: form.email.trim(), password: form.password, requested_role: form.requested_role });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-[440px] w-full bg-white border border-slate-200 rounded-2xl p-8 text-center shadow-sm">
          <div className="w-16 h-16 mx-auto rounded-full grid place-items-center mb-3" style={{ backgroundColor: '#dcf7e7' }}>
            <CheckCircle2 className="w-8 h-8" style={{ color: '#2ea690' }} />
          </div>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Registration submitted</h2>
          <p className="mt-2 text-slate-600 text-sm">
            Your account is pending admin approval. You&apos;ll be able to sign in once an administrator activates your account.
          </p>
          <button
            onClick={() => nav('/portal/login')}
            className="mt-6 w-full h-11 rounded-md text-white font-semibold hover:brightness-110 transition"
            style={{ backgroundColor: '#1e73be' }}
          >
            Go to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
      <div className="w-full max-w-[480px] bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
        <h2 className="font-heading text-2xl font-bold text-slate-900">Request access</h2>
        <p className="text-slate-600 text-sm mt-1">Create your account. An admin will approve it before you can sign in.</p>
        <form className="mt-6 space-y-4" onSubmit={submit}>
          <Field icon={User} label="Full Name" value={form.name} onChange={(v) => set('name', v)} placeholder="Your name" required />
          <Field icon={Mail} label="Work Email" type="email" value={form.email} onChange={(v) => set('email', v)} placeholder="you@finirio.com" required />
          <div>
            <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">Requested Role</label>
            <div className="flex items-center border border-slate-200 rounded-md overflow-hidden focus-within:border-[#1e73be] focus-within:ring-2 focus-within:ring-[#1e73be]/20 transition">
              <span className="px-3 h-11 grid place-items-center bg-slate-50 border-r border-slate-200 text-slate-500">
                <Briefcase className="w-4 h-4" />
              </span>
              <select
                value={form.requested_role}
                onChange={(e) => set('requested_role', e.target.value)}
                className="flex-1 h-11 px-3 outline-none bg-white text-slate-800"
              >
                {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </div>
          </div>
          <Field icon={Lock} label="Password" type="password" value={form.password} onChange={(v) => set('password', v)} placeholder="At least 6 characters" required />
          <Field icon={Lock} label="Confirm Password" type="password" value={form.confirm} onChange={(v) => set('confirm', v)} placeholder="Re-enter password" required />
          {err && <p className="text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</p>}
          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-md text-white font-semibold hover:brightness-110 transition disabled:opacity-70 inline-flex items-center justify-center gap-2"
            style={{ backgroundColor: '#1e73be' }}
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} {loading ? 'Submitting...' : 'Request access'}
          </button>
        </form>
        <div className="mt-5 text-center text-sm text-slate-600">
          Already have access?{' '}
          <Link to="/portal/login" className="text-[#1e73be] font-semibold hover:underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
};

const Field = ({ icon: Icon, label, value, onChange, type = 'text', placeholder, required }) => (
  <div>
    <label className="block text-slate-700 text-[13px] font-semibold mb-1.5">{label}</label>
    <div className="flex items-center border border-slate-200 rounded-md overflow-hidden focus-within:border-[#1e73be] focus-within:ring-2 focus-within:ring-[#1e73be]/20 transition">
      <span className="px-3 h-11 grid place-items-center bg-slate-50 border-r border-slate-200 text-slate-500">
        <Icon className="w-4 h-4" />
      </span>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="flex-1 h-11 px-3 outline-none text-slate-800"
      />
    </div>
  </div>
);

export default PortalRegister;
